import { useEffect, useState } from 'react'
import { ResponsiveImage } from './ResponsiveImage'

type HeroVideoProps = {
  mp4: string
  webm?: string
  poster: string
  fallbackImage: string
  alt: string
  className?: string
}

/**
 * Autoplaying hero video with graceful fallbacks:
 * - prefers-reduced-motion users see the still image
 * - mobile/low-power users see the still image
 * - otherwise MP4 plays, with optional WebM fallback
 */
export function HeroVideo({ mp4, webm, poster, fallbackImage, alt, className = '' }: HeroVideoProps) {
  const [useVideo, setUseVideo] = useState(true)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    if (mq.matches) setUseVideo(false)
    const onChange = (e: MediaQueryListEvent) => setUseVideo(!e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  if (!useVideo) {
    return (
      <ResponsiveImage
        src={fallbackImage}
        alt={alt}
        loading="eager"
        decoding="sync"
        className={className}
      />
    )
  }

  return (
    <video
      autoPlay
      muted
      loop
      playsInline
      poster={poster}
      className={className}
      aria-label={alt}
    >
      {webm && <source src={webm} type="video/webm" />}
      <source src={mp4} type="video/mp4" />
      <ResponsiveImage
        src={fallbackImage}
        alt={alt}
        loading="eager"
        decoding="sync"
        className="h-full w-full object-cover"
      />
    </video>
  )
}
